import React, { useEffect, useRef, useState } from "react";
import { NavLink, Routes, Route, Navigate } from "react-router-dom";
import { Page, PageHeader } from "../../components/PageHeader";
import { api, extractError } from "../../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../../lib/format";
import { toast } from "sonner";
import { Plus, Users, FileText, LogOut, Settings2, FileDown, Briefcase, Palmtree, Pencil, Trash2, Upload, FileSpreadsheet } from "lucide-react";
import { useReadOnly } from "../../hooks/useReadOnly";
import { usePermissions } from "../../hooks/usePermissions";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { LibroPagination, useClientPagination } from "../../components/LibroPagination";

const REM_BASE = "/app/remuneraciones";

const TABS = [
  { to: "trabajadores", label: "Trabajadores", icon: Users },
  { to: "liquidaciones", label: "Liquidaciones", icon: FileText },
  { to: "contratos", label: "Contratos", icon: Briefcase },
  { to: "vacaciones", label: "Vacaciones", icon: Palmtree },
  { to: "finiquitos", label: "Finiquitos", icon: LogOut },
  { to: "parametros", label: "Parámetros", icon: Settings2 },
];

async function downloadBlob(url, filename) {
  const { data } = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

const EMPTY_TRAB = {
  rut: "",
  nombres: "",
  apellidos: "",
  email: "",
  cargo: "",
  fecha_ingreso: todayISO().slice(0, 10),
  tipo_contrato: "indefinido",
  sueldo_base: 500000,
  gratificacion_legal: true,
  colacion: 0,
  movilizacion: 0,
  afp: "Modelo",
  tasa_afp: 11.44,
  salud_tipo: "fonasa",
  salud_pct: 7,
  cargas_familiares: 0,
  dias_vacaciones_pendientes: 15,
  portal_pin: "",
  activo: true,
};

function trabajadorToForm(t) {
  return {
    rut: t.rut || "",
    nombres: t.nombres || "",
    apellidos: t.apellidos || "",
    email: t.email || "",
    cargo: t.cargo || "",
    fecha_ingreso: (t.fecha_ingreso || todayISO()).slice(0, 10),
    tipo_contrato: t.tipo_contrato || "indefinido",
    sueldo_base: t.sueldo_base ?? 0,
    gratificacion_legal: t.gratificacion_legal !== false,
    colacion: t.colacion ?? 0,
    movilizacion: t.movilizacion ?? 0,
    afp: t.afp || "Modelo",
    tasa_afp: t.tasa_afp ?? 11.44,
    salud_tipo: t.salud_tipo || "fonasa",
    salud_pct: t.salud_pct ?? 7,
    cargas_familiares: t.cargas_familiares ?? 0,
    dias_vacaciones_pendientes: t.dias_vacaciones_pendientes ?? 0,
    portal_pin: "",
    activo: t.activo !== false,
  };
}

function TrabajadorFormFields({ form, setForm, isEdit }) {
  return (
  <>
    <div className="grid grid-cols-2 gap-2">
      <input required placeholder="RUT" value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
      <input placeholder="Cargo" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
    </div>
    <input required placeholder="Nombres" value={form.nombres} onChange={(e) => setForm({ ...form, nombres: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
    <input required placeholder="Apellidos" value={form.apellidos} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
    <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
    <input type="date" required value={form.fecha_ingreso} onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
    <select value={form.tipo_contrato} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm">
      <option value="indefinido">Indefinido</option>
      <option value="plazo_fijo">Plazo fijo</option>
    </select>
    <input type="number" required min={0} placeholder="Sueldo base" value={form.sueldo_base} onChange={(e) => setForm({ ...form, sueldo_base: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
    <div className="grid grid-cols-2 gap-2">
      <input type="number" min={0} placeholder="Colación" value={form.colacion} onChange={(e) => setForm({ ...form, colacion: +e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
      <input type="number" min={0} placeholder="Movilización" value={form.movilizacion} onChange={(e) => setForm({ ...form, movilizacion: +e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
    </div>
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={form.gratificacion_legal} onChange={(e) => setForm({ ...form, gratificacion_legal: e.target.checked })} />
      Gratificación legal
    </label>
    <div className="grid grid-cols-2 gap-2">
      <input placeholder="AFP" value={form.afp} onChange={(e) => setForm({ ...form, afp: e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
      <input type="number" step="0.01" placeholder="Tasa AFP %" value={form.tasa_afp} onChange={(e) => setForm({ ...form, tasa_afp: +e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <select value={form.salud_tipo} onChange={(e) => setForm({ ...form, salud_tipo: e.target.value })} className="border rounded-sm px-3 py-2 text-sm">
        <option value="fonasa">Fonasa</option>
        <option value="isapre">Isapre</option>
      </select>
      <input type="number" step="0.01" placeholder="Salud %" value={form.salud_pct} onChange={(e) => setForm({ ...form, salud_pct: +e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <input type="number" min={0} placeholder="Cargas familiares" value={form.cargas_familiares} onChange={(e) => setForm({ ...form, cargas_familiares: +e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
      <input type="number" min={0} step="0.5" placeholder="Días vacaciones pend." value={form.dias_vacaciones_pendientes} onChange={(e) => setForm({ ...form, dias_vacaciones_pendientes: +e.target.value })} className="border rounded-sm px-3 py-2 text-sm" />
    </div>
    <input
      type="password"
      placeholder={isEdit ? "Nuevo PIN portal (dejar vacío para no cambiar)" : "PIN portal colaborador (opcional)"}
      value={form.portal_pin}
      onChange={(e) => setForm({ ...form, portal_pin: e.target.value })}
      className="w-full border rounded-sm px-3 py-2 text-sm"
      minLength={form.portal_pin ? 4 : undefined}
      maxLength={8}
    />
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
      Activo
    </label>
  </>
  );
}

function periodoActual() {
  return todayISO().slice(0, 7);
}

function TrabajadoresTab({ canWrite }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_TRAB);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reemplazarImport, setReemplazarImport] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const importRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/remuneraciones/trabajadores");
    setList(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(extractError(e)));
  }, []);

  const listPage = useClientPagination(list, String(list.length));

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_TRAB);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm(trabajadorToForm(t));
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
    setForm(EMPTY_TRAB);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.portal_pin) delete payload.portal_pin;
    try {
      if (editingId) {
        await api.put(`/remuneraciones/trabajadores/${editingId}`, payload);
        toast.success("Trabajador actualizado");
      } else {
        await api.post("/remuneraciones/trabajadores", payload);
        toast.success("Trabajador creado");
      }
      closeDialog();
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const confirmDelete = async (forzarInactivo = false) => {
    if (!deleteTarget) return;
    try {
      const params = forzarInactivo ? "?forzar_inactivo=true" : "";
      const { data } = await api.delete(`/remuneraciones/trabajadores/${deleteTarget.id}${params}`);
      toast.success(data.inactivado ? "Trabajador marcado como inactivo" : "Trabajador eliminado");
      setDeleteTarget(null);
      load();
    } catch (err) {
      const msg = extractError(err);
      if (!forzarInactivo && msg.includes("liquidaciones")) {
        setDeleteTarget({ ...deleteTarget, suggestInactivo: true });
        return;
      }
      toast.error(msg);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.info("Importando dotación…");
      const { data } = await api.post(
        `/remuneraciones/trabajadores/import?reemplazar=${reemplazarImport}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportReport(data);
      const parts = [
        `Nuevos: ${data.imported}`,
        data.updated ? `Actualizados: ${data.updated}` : null,
        `Omitidos: ${data.skipped}`,
      ].filter(Boolean);
      if (data.imported > 0 || data.updated > 0) {
        toast.success(parts.join(" · "));
      } else if (data.errors?.length) {
        toast.error(`No se importó ningún trabajador. ${data.errors.length} fila(s) con error.`);
      } else {
        toast.info(parts.join(" · ") || "Sin cambios");
      }
      if (data.errors?.length && (data.imported > 0 || data.updated > 0)) {
        toast.warning(`${data.errors.length} fila(s) con error — ver detalle abajo`);
      }
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  };

  return (
    <section className="bg-card border border-border rounded-md overflow-hidden">
      <div className="flex flex-wrap justify-between items-center gap-2 p-4 border-b border-border">
        <h2 className="font-display text-lg font-medium">Dotación</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => downloadBlob("/remuneraciones/trabajadores/export", `dotacion_${todayISO().slice(0, 10)}.xlsx`)}
            disabled={list.length === 0}
            title={list.length === 0 ? "No hay trabajadores para exportar" : "Descargar dotación actual"}
            className="inline-flex items-center gap-1 border border-border text-sm px-3 py-2 rounded-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDown className="h-4 w-4" /> Exportar dotación
          </button>
          {canWrite && (
            <>
            <button
              type="button"
              onClick={() => downloadBlob("/remuneraciones/trabajadores/plantilla", "plantilla_dotacion_ejemplo.xlsx")}
              className="inline-flex items-center gap-1 border border-border text-sm px-3 py-2 rounded-sm hover:bg-muted"
            >
              <FileSpreadsheet className="h-4 w-4" /> Plantilla con ejemplos
            </button>
            <label className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <input type="checkbox" checked={reemplazarImport} onChange={(e) => setReemplazarImport(e.target.checked)} />
              Reemplazar por RUT
            </label>
            <button
              type="button"
              onClick={() => importRef.current?.click()}
              className="inline-flex items-center gap-1 border border-primary text-primary text-sm px-3 py-2 rounded-sm hover:bg-primary/10"
            >
              <Upload className="h-4 w-4" /> Importar Excel
            </button>
            <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-3 py-2 rounded-sm"
            >
              <Plus className="h-4 w-4" /> Nuevo trabajador
            </button>
            </>
          )}
        </div>
      </div>

      {importReport?.errors?.length > 0 && (
        <div className="mx-4 mb-4 bg-warning-surface border border-warning rounded-md p-4">
          <div className="flex justify-between items-start gap-2">
            <div>
              <div className="label-caps text-warning-foreground">Errores de importación</div>
              <p className="text-xs text-muted-foreground mt-1">
                Nuevos: {importReport.imported} · Actualizados: {importReport.updated || 0} · Omitidos:{" "}
                {importReport.skipped}
              </p>
            </div>
            <button type="button" className="text-sm text-muted-foreground" onClick={() => setImportReport(null)}>
              ✕
            </button>
          </div>
          <ul className="mt-2 text-sm text-destructive list-disc pl-5 space-y-1 max-h-40 overflow-y-auto">
            {importReport.errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-2">
            Use la hoja <strong>Dotacion</strong> y fechas en formato <strong>AAAA-MM-DD</strong> (ej. 2024-01-15).
            Vuelva a descargar la plantilla si las columnas tienen otro nombre.
          </p>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar trabajador" : "Alta de trabajador"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <TrabajadorFormFields form={form} setForm={setForm} isEdit={!!editingId} />
            <DialogFooter>
              <button type="button" onClick={closeDialog} className="text-sm px-4 py-2 border rounded-sm">
                Cancelar
              </button>
              <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                {editingId ? "Guardar cambios" : "Crear"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.suggestInactivo ? "No se puede eliminar" : "Eliminar trabajador"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.suggestInactivo ? (
                <>
                  <strong>
                    {deleteTarget.nombres} {deleteTarget.apellidos}
                  </strong>{" "}
                  tiene liquidaciones. Puede marcarlo como inactivo en su lugar.
                </>
              ) : (
                <>
                  ¿Eliminar a <strong>{deleteTarget?.nombres} {deleteTarget?.apellidos}</strong> ({deleteTarget?.rut})?
                  Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {deleteTarget?.suggestInactivo ? (
              <AlertDialogAction onClick={() => confirmDelete(true)} className="bg-warning-foreground">
                Marcar inactivo
              </AlertDialogAction>
            ) : (
              <AlertDialogAction onClick={() => confirmDelete(false)} className="bg-red-700">
                Eliminar
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <table className="acc-table">
        <thead>
          <tr>
            <th>RUT</th>
            <th>Nombre</th>
            <th>Cargo</th>
            <th>Sueldo base</th>
            <th>Estado</th>
            {canWrite && <th style={{ width: 88 }} />}
          </tr>
        </thead>
        <tbody>
          {listPage.pageItems.map((t) => (
            <tr key={t.id}>
              <td className="font-mono text-sm">{t.rut}</td>
              <td>
                {t.nombres} {t.apellidos}
              </td>
              <td>{t.cargo}</td>
              <td>{fmtCLP(t.sueldo_base)}</td>
              <td>{t.activo ? "Activo" : "Inactivo"}</td>
              {canWrite && (
                <td>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      title="Editar"
                      onClick={() => openEdit(t)}
                      className="p-1.5 text-muted-foreground hover:text-primary hover:bg-muted rounded-sm"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Eliminar"
                      onClick={() => setDeleteTarget({ ...t, suggestInactivo: false })}
                      className="p-1.5 text-destructive hover:bg-destructive/10 rounded-sm"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {list.length === 0 && (
        <p className="p-8 text-center text-sm text-muted-foreground">
          Sin trabajadores. Use <strong>Plantilla con ejemplos</strong> (5 trabajadores de muestra) e <strong>Importar Excel</strong>,
          o cree uno con <strong>Nuevo trabajador</strong>.
        </p>
      )}
      {list.length > 0 && (
        <LibroPagination
          pagination={listPage.pagination}
          pageSize={listPage.pageSize}
          onPageSizeChange={listPage.setPageSize}
          onPageChange={listPage.setPage}
          testidPrefix="rem-trab"
        />
      )}
    </section>
  );
}

function LiquidacionesTab({ canWrite }) {
  const [list, setList] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [periodo, setPeriodo] = useState(periodoActual());

  const load = async () => {
    const { data } = await api.get("/remuneraciones/liquidaciones");
    setList(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(extractError(e)));
  }, []);

  const listPage = useClientPagination(list, String(list.length));
  const detalleLineas = detalle?.lineas || [];
  const detallePage = useClientPagination(detalleLineas, detalle?.id || "");

  const generar = async () => {
    try {
      const { data } = await api.post("/remuneraciones/liquidaciones/generar", { periodo });
      toast.success(`Liquidación ${periodo} generada (${data.n_trabajadores} trabajadores)`);
      setDetalle(data);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const ver = async (id) => {
    const { data } = await api.get(`/remuneraciones/liquidaciones/${id}`);
    setDetalle(data);
  };

  const cerrar = async (id) => {
    try {
      const { data } = await api.post(`/remuneraciones/liquidaciones/${id}/cerrar`);
      toast.success(
        data.asiento_generado
          ? "Liquidación cerrada y asiento contable generado"
          : "Liquidación cerrada"
      );
      load();
      ver(id);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="space-y-4">
      <section className="bg-card border border-border rounded-md p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="label-caps block mb-1">Período (AAAA-MM)</label>
          <input
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            pattern="\d{4}-\d{2}"
            className="border rounded-sm px-3 py-2 text-sm font-mono"
          />
        </div>
        {canWrite && (
          <button onClick={generar} className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
            Generar / recalcular liquidación
          </button>
        )}
      </section>

      <section className="bg-card border border-border rounded-md overflow-hidden">
        <table className="acc-table">
          <thead>
            <tr>
              <th>Período</th>
              <th>Estado</th>
              <th>Trabajadores</th>
              <th>Líquido total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {listPage.pageItems.map((l) => (
              <tr key={l.id}>
                <td className="font-medium">{l.periodo}</td>
                <td>{l.estado}</td>
                <td>{l.n_trabajadores}</td>
                <td>{fmtCLP(l.total_liquido)}</td>
                <td className="space-x-2">
                  <button type="button" onClick={() => ver(l.id)} className="text-sm text-primary underline">
                    Ver
                  </button>
                  {canWrite && l.estado === "borrador" && (
                    <button type="button" onClick={() => cerrar(l.id)} className="text-sm text-primary underline">
                      Cerrar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length > 0 && (
          <LibroPagination
            pagination={listPage.pagination}
            pageSize={listPage.pageSize}
            onPageSizeChange={listPage.setPageSize}
            onPageChange={listPage.setPage}
            testidPrefix="rem-liq"
          />
        )}
      </section>

      {detalle && (
        <section className="bg-card border border-border rounded-md p-4">
          <h3 className="font-medium mb-2">
            Detalle {detalle.periodo} — {detalle.estado}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Haberes: {fmtCLP(detalle.total_haberes)} · Descuentos: {fmtCLP(detalle.total_descuentos)} · Líquido:{" "}
            {fmtCLP(detalle.total_liquido)}
            {detalle.asiento_id && (
              <span className="block mt-1">Asiento contable: {detalle.asiento_id.slice(0, 8)}…</span>
            )}
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm border border-border px-3 py-1.5 rounded-sm"
              onClick={() =>
                downloadBlob(
                  `/remuneraciones/liquidaciones/${detalle.id}/export/previred`,
                  `previred_${detalle.periodo}.txt`
                ).catch((e) => toast.error(extractError(e)))
              }
            >
              <FileDown className="h-3.5 w-3.5" /> Previred
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm border border-border px-3 py-1.5 rounded-sm"
              onClick={() =>
                downloadBlob(
                  `/remuneraciones/liquidaciones/${detalle.id}/export/lre`,
                  `LRE_${detalle.periodo}.csv`
                ).catch((e) => toast.error(extractError(e)))
              }
            >
              <FileDown className="h-3.5 w-3.5" /> LRE
            </button>
            {detalle.estado === "cerrada" && (
              <>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm border border-success text-success px-3 py-1.5 rounded-sm hover:bg-success/10"
                  onClick={() =>
                    downloadBlob(`/remuneraciones/libro/${detalle.periodo}/excel`, `libro_rem_${detalle.periodo}.xlsx`)
                      .catch((e) => toast.error(extractError(e)))
                  }
                >
                  <FileDown className="h-3.5 w-3.5" /> Libro Excel
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm border border-success text-success px-3 py-1.5 rounded-sm hover:bg-success/10"
                  onClick={() =>
                    downloadBlob(`/remuneraciones/libro/${detalle.periodo}/pdf`, `libro_rem_${detalle.periodo}.pdf`)
                      .catch((e) => toast.error(extractError(e)))
                  }
                >
                  <FileDown className="h-3.5 w-3.5" /> Libro PDF
                </button>
              </>
            )}
          </div>
          <table className="acc-table text-sm">
            <thead>
              <tr>
                <th>Trabajador</th>
                <th>Imp. único</th>
                <th>Haberes</th>
                <th>Descuentos</th>
                <th>Líquido</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {detallePage.pageItems.map((ln) => (
                <tr key={ln.trabajador_id}>
                  <td>
                    {ln.nombre}
                    <span className="block text-[10px] text-muted-foreground/70 font-mono">{ln.rut}</span>
                  </td>
                  <td>{fmtCLP(ln.impuesto_unico || 0)}</td>
                  <td>{fmtCLP(ln.total_haberes)}</td>
                  <td>{fmtCLP(ln.total_descuentos)}</td>
                  <td className="font-medium">{fmtCLP(ln.liquido_a_pagar)}</td>
                  <td>
                    <button
                      type="button"
                      className="text-primary underline text-xs"
                      onClick={() =>
                        downloadBlob(
                          `/remuneraciones/liquidaciones/${detalle.id}/certificado/${ln.trabajador_id}`,
                          `certificado_${detalle.periodo}.pdf`
                        ).catch((e) => toast.error(extractError(e)))
                      }
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {detalleLineas.length > 0 && (
            <LibroPagination
              pagination={detallePage.pagination}
              pageSize={detallePage.pageSize}
              onPageSizeChange={detallePage.setPageSize}
              onPageChange={detallePage.setPage}
              testidPrefix="rem-liq-detalle"
            />
          )}
        </section>
      )}
    </div>
  );
}

function FiniquitosTab({ canWrite }) {
  const [trabajadores, setTrabajadores] = useState([]);
  const [finiquitos, setFiniquitos] = useState([]);
  const [form, setForm] = useState({
    trabajador_id: "",
    fecha_termino: todayISO().slice(0, 10),
    causal: "necesidades_empresa",
    dias_vacaciones_pendientes: 0,
    incluir_indemnizacion: true,
  });
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, f] = await Promise.all([
          api.get("/remuneraciones/trabajadores", { params: { activos: true } }),
          api.get("/remuneraciones/finiquitos"),
        ]);
        setTrabajadores(t.data);
        setFiniquitos(f.data);
      } catch (e) {
        toast.error(extractError(e));
      }
    })();
  }, []);

  const calcular = async () => {
    try {
      const { data } = await api.post("/remuneraciones/finiquitos/calcular", form);
      setPreview(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const guardar = async () => {
    try {
      await api.post("/remuneraciones/finiquitos", form);
      toast.success("Finiquito registrado");
      setPreview(null);
      const { data } = await api.get("/remuneraciones/finiquitos");
      setFiniquitos(data);
      const { data: t } = await api.get("/remuneraciones/trabajadores", { params: { activos: true } });
      setTrabajadores(t);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <section className="bg-card border border-border rounded-md p-4 space-y-3">
        <h2 className="font-display text-lg font-medium">Nuevo finiquito</h2>
        <select
          value={form.trabajador_id}
          onChange={(e) => setForm({ ...form, trabajador_id: e.target.value })}
          className="w-full border rounded-sm px-3 py-2 text-sm"
        >
          <option value="">Seleccione trabajador</option>
          {trabajadores.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombres} {t.apellidos} — {t.rut}
            </option>
          ))}
        </select>
        <input type="date" value={form.fecha_termino} onChange={(e) => setForm({ ...form, fecha_termino: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        <input type="number" min={0} placeholder="Días vacaciones pendientes" value={form.dias_vacaciones_pendientes} onChange={(e) => setForm({ ...form, dias_vacaciones_pendientes: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.incluir_indemnizacion} onChange={(e) => setForm({ ...form, incluir_indemnizacion: e.target.checked })} />
          Incluir indemnización por años de servicio
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={calcular} className="border border-border px-3 py-2 text-sm rounded-sm">
            Calcular
          </button>
          {canWrite && preview && (
            <button type="button" onClick={guardar} className="bg-primary text-primary-foreground px-3 py-2 text-sm rounded-sm">
              Guardar finiquito
            </button>
          )}
        </div>
        {preview && (
          <div className="text-sm border-t pt-3 space-y-1">
            <div className="font-medium">{preview.nombre}</div>
            {preview.haberes?.map((h) => (
              <div key={h.codigo} className="flex justify-between">
                <span>{h.nombre}</span>
                <span>{fmtCLP(h.monto)}</span>
              </div>
            ))}
            <div className="flex justify-between font-medium pt-2 border-t">
              <span>Líquido finiquito</span>
              <span>{fmtCLP(preview.liquido_a_pagar)}</span>
            </div>
          </div>
        )}
      </section>
      <section className="bg-card border border-border rounded-md overflow-hidden">
        <h2 className="font-display text-lg font-medium p-4 border-b">Historial</h2>
        <table className="acc-table text-sm">
          <thead>
            <tr>
              <th>Trabajador</th>
              <th>Término</th>
              <th>Líquido</th>
            </tr>
          </thead>
          <tbody>
            {finiquitos.map((f) => (
              <tr key={f.id}>
                <td>{f.nombre}</td>
                <td>{fmtDate(f.fecha_termino)}</td>
                <td>{fmtCLP(f.liquido_a_pagar)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ContratosTab({ canWrite }) {
  const [trabajadores, setTrabajadores] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [form, setForm] = useState({
    trabajador_id: "",
    tipo: "contrato",
    fecha_inicio: todayISO().slice(0, 10),
    fecha_fin: "",
    sueldo_base: 500000,
    jornada: "completa",
    observaciones: "",
  });

  const load = async () => {
    const [t, c] = await Promise.all([
      api.get("/remuneraciones/trabajadores"),
      api.get("/remuneraciones/contratos"),
    ]);
    setTrabajadores(t.data);
    setContratos(c.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(extractError(e)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/remuneraciones/contratos", {
        ...form,
        fecha_fin: form.fecha_fin || null,
        sueldo_base: form.sueldo_base || null,
      });
      toast.success("Registro guardado");
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const nombreTrab = (id) => {
    const t = trabajadores.find((x) => x.id === id);
    return t ? `${t.nombres} ${t.apellidos}` : id;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {canWrite && (
        <section className="bg-card border border-border rounded-md p-4 space-y-3">
          <h2 className="font-display text-lg font-medium">Contrato o anexo</h2>
          <form onSubmit={submit} className="space-y-3">
            <select
              required
              value={form.trabajador_id}
              onChange={(e) => setForm({ ...form, trabajador_id: e.target.value })}
              className="w-full border rounded-sm px-3 py-2 text-sm"
            >
              <option value="">Trabajador</option>
              {trabajadores.filter((t) => t.activo).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombres} {t.apellidos}
                </option>
              ))}
            </select>
            <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm">
              <option value="contrato">Contrato</option>
              <option value="anexo">Anexo</option>
            </select>
            <input type="date" required value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
            <input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" placeholder="Fin (opcional)" />
            <input type="number" value={form.sueldo_base} onChange={(e) => setForm({ ...form, sueldo_base: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" placeholder="Sueldo" />
            <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
              Guardar
            </button>
          </form>
        </section>
      )}
      <section className={`bg-card border border-border rounded-md overflow-hidden ${canWrite ? "" : "lg:col-span-2"}`}>
        <table className="acc-table text-sm">
          <thead>
            <tr>
              <th>Trabajador</th>
              <th>Tipo</th>
              <th>Inicio</th>
              <th>Sueldo</th>
            </tr>
          </thead>
          <tbody>
            {contratos.map((c) => (
              <tr key={c.id}>
                <td>{nombreTrab(c.trabajador_id)}</td>
                <td>{c.tipo}</td>
                <td>{fmtDate(c.fecha_inicio)}</td>
                <td>{c.sueldo_base ? fmtCLP(c.sueldo_base) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function VacacionesTab({ canWrite }) {
  const [trabajadores, setTrabajadores] = useState([]);
  const [vacaciones, setVacaciones] = useState([]);
  const [form, setForm] = useState({
    trabajador_id: "",
    fecha_inicio: todayISO().slice(0, 10),
    fecha_fin: todayISO().slice(0, 10),
    dias: 5,
    observaciones: "",
  });

  const load = async () => {
    const [t, v] = await Promise.all([
      api.get("/remuneraciones/trabajadores", { params: { activos: true } }),
      api.get("/remuneraciones/vacaciones"),
    ]);
    setTrabajadores(t.data);
    setVacaciones(v.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(extractError(e)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/remuneraciones/vacaciones", form);
      toast.success("Vacación registrada");
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const setEstado = async (id, estado) => {
    try {
      await api.patch(`/remuneraciones/vacaciones/${id}`, null, { params: { estado } });
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const nombreTrab = (id) => {
    const t = trabajadores.find((x) => x.id === id);
    return t ? `${t.nombres} ${t.apellidos}` : id;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {canWrite && (
        <section className="bg-card border border-border rounded-md p-4 space-y-3">
          <h2 className="font-display text-lg font-medium">Solicitud de vacaciones</h2>
          <form onSubmit={submit} className="space-y-3">
            <select
              required
              value={form.trabajador_id}
              onChange={(e) => setForm({ ...form, trabajador_id: e.target.value })}
              className="w-full border rounded-sm px-3 py-2 text-sm"
            >
              <option value="">Trabajador</option>
              {trabajadores.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombres} {t.apellidos} ({t.dias_vacaciones_pendientes ?? 0} días)
                </option>
              ))}
            </select>
            <input type="date" required value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
            <input type="date" required value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
            <input type="number" min={0.5} step={0.5} value={form.dias} onChange={(e) => setForm({ ...form, dias: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
            <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
              Registrar
            </button>
          </form>
        </section>
      )}
      <section className={`bg-card border border-border rounded-md overflow-hidden ${canWrite ? "" : "lg:col-span-2"}`}>
        <table className="acc-table text-sm">
          <thead>
            <tr>
              <th>Trabajador</th>
              <th>Desde</th>
              <th>Días</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {vacaciones.map((v) => (
              <tr key={v.id}>
                <td>{nombreTrab(v.trabajador_id)}</td>
                <td>{fmtDate(v.fecha_inicio)}</td>
                <td>{v.dias}</td>
                <td>{v.estado}</td>
                <td className="space-x-1">
                  {canWrite && v.estado === "solicitada" && (
                    <>
                      <button type="button" className="text-xs underline" onClick={() => setEstado(v.id, "aprobada")}>
                        Aprobar
                      </button>
                      <button type="button" className="text-xs underline text-destructive" onClick={() => setEstado(v.id, "rechazada")}>
                        Rechazar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ParametrosTab({ canWrite }) {
  const [periodo, setPeriodo] = useState(periodoActual());
  const [form, setForm] = useState({
    periodo: periodoActual(),
    uf: 38000,
    utm: 67000,
    imm: 529000,
    tope_imponible_uf: 81.6,
    tasa_sis: 1.49,
    dias_mes: 30,
  });

  const load = async (p) => {
    const { data } = await api.get("/remuneraciones/parametros", { params: { periodo: p } });
    setForm({ ...data, periodo: p });
  };

  useEffect(() => {
    load(periodo).catch((e) => toast.error(extractError(e)));
  }, [periodo]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/remuneraciones/parametros", form);
      toast.success("Parámetros guardados");
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <section className="bg-card border border-border rounded-md p-6 max-w-xl">
      <h2 className="font-display text-lg font-medium mb-2">Parámetros del período</h2>
      <p className="text-sm text-muted-foreground mb-4">
        UF, UTM, IMM y topes imponibles usados en liquidaciones y finiquitos (normativa Chile).
      </p>
      <form onSubmit={save} className="space-y-3">
        <input value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="w-full border rounded-sm px-3 py-2 text-sm font-mono" />
        <input type="number" placeholder="UF" value={form.uf} onChange={(e) => setForm({ ...form, uf: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        <input type="number" placeholder="UTM" value={form.utm} onChange={(e) => setForm({ ...form, utm: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        <input type="number" placeholder="IMM" value={form.imm} onChange={(e) => setForm({ ...form, imm: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        <input type="number" step="0.1" placeholder="Tope imponible (UF)" value={form.tope_imponible_uf} onChange={(e) => setForm({ ...form, tope_imponible_uf: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        <input type="number" placeholder="Días del mes" value={form.dias_mes} onChange={(e) => setForm({ ...form, dias_mes: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        <input type="number" step="0.01" placeholder="Tasa SIS %" value={form.tasa_sis ?? 1.49} onChange={(e) => setForm({ ...form, tasa_sis: +e.target.value })} className="w-full border rounded-sm px-3 py-2 text-sm" />
        {form.tabla_impuesto_unico?.length > 0 && (
          <div className="text-xs border rounded-sm p-3 bg-background space-y-1 max-h-40 overflow-y-auto">
            <p className="font-medium text-primary mb-1">Tabla impuesto único (UTM)</p>
            {form.tabla_impuesto_unico.map((t, i) => (
              <div key={i} className="flex justify-between font-mono">
                <span>Hasta {t.hasta_utm} UTM</span>
                <span>
                  {((t.factor || 0) * 100).toFixed(1)}% − rebaja {t.rebaja_utm} UTM
                </span>
              </div>
            ))}
          </div>
        )}
        {canWrite && (
          <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
            Guardar parámetros
          </button>
        )}
      </form>
    </section>
  );
}

export default function Remuneraciones() {
  const readOnly = useReadOnly();
  const { canWrite } = usePermissions();
  const canEdit = !readOnly && canWrite("remuneraciones");

  return (
    <Page>
      <PageHeader
        title="Remuneraciones"
        subtitle="Nómina Chile: Previred, LRE, impuesto único, contratos, asiento al cerrar y portal del colaborador"
        testid="remuneraciones-header"
      />

      <p className="text-sm text-muted-foreground mb-4">
        Portal colaborador:{" "}
        <a href="/portal-colaborador" target="_blank" rel="noreferrer" className="text-primary underline">
          /portal-colaborador
        </a>{" "}
        (RUT + PIN definido al crear el trabajador).
      </p>

      <nav className="flex flex-wrap gap-1 mb-6 border-b border-border">
        {TABS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={`${REM_BASE}/${to}`}
            end
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ${
                isActive
                  ? "border-primary text-primary font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<Navigate to={`${REM_BASE}/trabajadores`} replace />} />
        <Route path="trabajadores" element={<TrabajadoresTab canWrite={canEdit} />} />
        <Route path="liquidaciones" element={<LiquidacionesTab canWrite={canEdit} />} />
        <Route path="contratos" element={<ContratosTab canWrite={canEdit} />} />
        <Route path="vacaciones" element={<VacacionesTab canWrite={canEdit} />} />
        <Route path="finiquitos" element={<FiniquitosTab canWrite={canEdit} />} />
        <Route path="parametros" element={<ParametrosTab canWrite={canEdit} />} />
        <Route path="*" element={<Navigate to={`${REM_BASE}/trabajadores`} replace />} />
      </Routes>
    </Page>
  );
}
